import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  RefreshCw, CalendarDays, TrendingUp, PoundSterling, Target,
  Clock, Trophy, Info, Award, ThumbsDown,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const gbp = (v) => {
  if (v === null || v === undefined) return '—';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(v);
};
const pct = (v) => (v === null || v === undefined ? '—' : `${v}%`);
const num = (v) => (v === null || v === undefined ? '—' : v);

const WINDOWS = [
  { key: 14, label: '14 Days' },
  { key: 28, label: '28 Days' },
];

const pctColor = (v) => (v >= 50 ? 'text-green-600' : v >= 25 ? 'text-amber-600' : 'text-red-600');

export default function CoachMetrics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(28);
  const [customOpen, setCustomOpen] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [activeCustom, setActiveCustom] = useState(null);

  const fetchData = useCallback(async (force = false) => {
    setLoading(true);
    try {
      let url = `${API}/api/lead-tracker/coach?days=${days}`;
      if (activeCustom) url = `${API}/api/lead-tracker/coach?start_date=${activeCustom.start}&end_date=${activeCustom.end}`;
      if (force) url += '&refresh=1';
      const res = await fetch(url);
      if (res.ok) setData(await res.json());
    } catch (e) {
      console.error('Failed to fetch coach metrics:', e); // eslint-disable-line no-console
    }
    setLoading(false);
  }, [days, activeCustom]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh ticker: while the snapshot is updating, quietly re-fetch until fresh.
  useEffect(() => {
    if (data?.updating) {
      const t = setTimeout(() => fetchData(), 8000);
      return () => clearTimeout(t);
    }
  }, [data?.updating, fetchData]);

  const applyCustom = () => { if (startDate && endDate) setActiveCustom({ start: startDate, end: endDate }); };
  const selectWindow = (k) => { setDays(k); setCustomOpen(false); setActiveCustom(null); };

  const closed = data?.closedInWindow || {};
  const closedRows = closed.byChannel || [];
  const closedTot = closed.totals || {};
  const cohort = data?.cohort || {};
  const cohortRows = cohort.byChannel || [];
  const cohortTot = cohort.totals || {};
  // Keep "won" consistent with the CLOSED box (deals won in the window),
  // not the cohort's created-in-window wins (which differ).
  const winsInWindow = closedTot.closes || 0;
  const bookingToClosePct = cohortTot.booked ? Math.round((winsInWindow / cohortTot.booked) * 1000) / 10 : 0;
  const hi = data?.highlights || {};
  const winReasons = data?.winReasons || {};
  const lostReasons = data?.lostReasons || {};
  const notes = data?.dataNotes || [];

  return (
    <Card className="bg-gradient-to-br from-stone-900 to-stone-800 border-stone-700 shadow-lg text-stone-100" data-testid="coach-metrics">
      <CardHeader className="pb-3 border-b border-stone-700">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-400" /> Coach Scorecard
            </CardTitle>
            <p className="text-xs text-stone-400 mt-0.5">Booked · Sat · Closed · Cost per close · Profit · Velocity{data?.cachedAt ? ` · cached ${new Date(data.cachedAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}` : ''}{data?.updating ? ' · updating…' : ''}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex bg-stone-700/60 rounded-lg p-0.5">
              {WINDOWS.map(w => (
                <button
                  key={w.key}
                  onClick={() => selectWindow(w.key)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    days === w.key && !activeCustom ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-300 hover:text-white'
                  }`}
                  data-testid={`coach-window-${w.key}`}
                >
                  {w.label}
                </button>
              ))}
              <button
                onClick={() => setCustomOpen(!customOpen)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1 ${
                  activeCustom ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-300 hover:text-white'
                }`}
                data-testid="coach-window-custom"
              >
                <CalendarDays className="w-3.5 h-3.5" /> Custom
              </button>
            </div>
            {customOpen && (
              <div className="flex items-center gap-1.5">
                <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="h-8 text-xs w-[130px] bg-stone-700 border-stone-600 text-white" />
                <span className="text-xs text-stone-400">to</span>
                <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="h-8 text-xs w-[130px] bg-stone-700 border-stone-600 text-white" />
                <Button onClick={applyCustom} disabled={!startDate || !endDate || loading} size="sm" className="bg-amber-500 text-stone-900 hover:bg-amber-400 h-8 text-xs" data-testid="coach-apply-custom">Apply</Button>
              </div>
            )}
            <Button onClick={() => fetchData(true)} disabled={loading} variant="outline" size="sm" className="border-stone-600 bg-stone-700/40 text-stone-200 hover:bg-stone-700" data-testid="coach-refresh" title="Refresh live from Brevo now">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-5 space-y-5">
        {/* Highlight cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <div className="bg-stone-800/60 rounded-xl p-4 border border-stone-700" data-testid="coach-closed-total">
            <div className="flex items-center gap-1.5 text-stone-400 text-xs uppercase tracking-wider"><Target className="w-3.5 h-3.5" /> Closed</div>
            <p className="text-3xl font-bold text-white mt-1">{num(closedTot.closes)}</p>
            <p className="text-xs text-green-400 mt-0.5">{gbp(closedTot.revenue)} revenue</p>
          </div>
          <div className="bg-stone-800/60 rounded-xl p-4 border border-stone-700">
            <div className="flex items-center gap-1.5 text-stone-400 text-xs uppercase tracking-wider"><PoundSterling className="w-3.5 h-3.5" /> Revenue</div>
            <p className="text-3xl font-bold text-amber-400 mt-1">{gbp(closedTot.revenue)}</p>
            <p className="text-xs text-stone-400 mt-0.5">booked in period</p>
          </div>
          <div className="bg-stone-800/60 rounded-xl p-4 border border-stone-700">
            <div className="flex items-center gap-1.5 text-stone-400 text-xs uppercase tracking-wider"><TrendingUp className="w-3.5 h-3.5" /> Booking→Close</div>
            <p className={`text-3xl font-bold mt-1 ${pctColor(bookingToClosePct)}`}>{pct(bookingToClosePct)}</p>
            <p className="text-xs text-stone-400 mt-0.5">{num(cohortTot.booked)} booked · {winsInWindow} won</p>
          </div>
          <div className="bg-stone-800/60 rounded-xl p-4 border border-stone-700">
            <div className="flex items-center gap-1.5 text-stone-400 text-xs uppercase tracking-wider"><Clock className="w-3.5 h-3.5" /> Avg Days to Close</div>
            <p className="text-3xl font-bold text-white mt-1">{num(closedTot.avgDaysToClose)}</p>
            <p className="text-xs text-stone-400 mt-0.5">lead → close</p>
          </div>
          <div className="bg-stone-800/60 rounded-xl p-4 border border-stone-700">
            <div className="flex items-center gap-1.5 text-stone-400 text-xs uppercase tracking-wider"><Award className="w-3.5 h-3.5" /> Top Channel</div>
            <p className="text-lg font-bold text-white mt-1 leading-tight">{hi.highestConverting?.channel || '—'}</p>
            <p className="text-xs text-green-400 mt-0.5">{hi.highestConverting ? `${pct(hi.highestConverting.leadToClose)} lead→close` : 'not enough data'}</p>
          </div>
        </div>

        {/* Closed in window table */}
        <div>
          <h4 className="text-sm font-semibold text-stone-200 mb-2">Closed in window — by channel</h4>
          <div className="overflow-x-auto rounded-lg border border-stone-700">
            <table className="w-full text-sm" data-testid="coach-closed-table">
              <thead className="bg-stone-800/80">
                <tr className="text-stone-400">
                  <th className="text-left py-2.5 px-3 font-medium">Channel</th>
                  <th className="text-right py-2.5 px-3 font-medium">Closes</th>
                  <th className="text-right py-2.5 px-3 font-medium">Revenue</th>
                  <th className="text-right py-2.5 px-3 font-medium">Spend</th>
                  <th className="text-right py-2.5 px-3 font-medium">Cost/Close</th>
                  <th className="text-right py-2.5 px-3 font-medium">Profit</th>
                  <th className="text-right py-2.5 px-3 font-medium">Avg Days</th>
                </tr>
              </thead>
              <tbody>
                {closedRows.length === 0 && (
                  <tr><td colSpan={7} className="py-4 px-3 text-center text-stone-500">No closes in this window</td></tr>
                )}
                {closedRows.map(r => (
                  <tr key={r.channel} className="border-t border-stone-700/70 hover:bg-stone-800/40">
                    <td className="py-2 px-3 font-medium text-white">{r.channel}</td>
                    <td className="py-2 px-3 text-right text-stone-200">{r.closes}</td>
                    <td className="py-2 px-3 text-right text-stone-200">{gbp(r.revenue)}</td>
                    <td className="py-2 px-3 text-right text-stone-400">{gbp(r.spend)}</td>
                    <td className="py-2 px-3 text-right text-stone-200">{r.costPerClose === null ? '—' : gbp(r.costPerClose)}</td>
                    <td className="py-2 px-3 text-right font-semibold text-amber-400">{gbp(r.profit)}</td>
                    <td className="py-2 px-3 text-right text-stone-400">{num(r.avgDaysToClose)}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-stone-600 bg-stone-800/60 font-bold text-white">
                  <td className="py-2.5 px-3">TOTAL</td>
                  <td className="py-2.5 px-3 text-right">{num(closedTot.closes)}</td>
                  <td className="py-2.5 px-3 text-right">{gbp(closedTot.revenue)}</td>
                  <td className="py-2.5 px-3 text-right text-stone-300">{gbp(closedTot.spend)}</td>
                  <td className="py-2.5 px-3 text-right">—</td>
                  <td className="py-2.5 px-3 text-right text-amber-400">{gbp(closedTot.profit)}{closedTot.spendComplete === false ? '*' : ''}</td>
                  <td className="py-2.5 px-3 text-right text-stone-300">{num(closedTot.avgDaysToClose)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Cohort funnel table */}
        <div>
          <h4 className="text-sm font-semibold text-stone-200 mb-2">Leads created in this window — how far they've got so far</h4>
          <div className="overflow-x-auto rounded-lg border border-stone-700">
            <table className="w-full text-sm" data-testid="coach-cohort-table">
              <thead className="bg-stone-800/80">
                <tr className="text-stone-400">
                  <th className="text-left py-2.5 px-3 font-medium">Channel</th>
                  <th className="text-right py-2.5 px-3 font-medium">Leads</th>
                  <th className="text-right py-2.5 px-3 font-medium">Booked</th>
                  <th className="text-right py-2.5 px-3 font-medium">Sat</th>
                  <th className="text-right py-2.5 px-3 font-medium">Closed</th>
                  <th className="text-right py-2.5 px-3 font-medium">Booked→Close</th>
                  <th className="text-right py-2.5 px-3 font-medium">Sat→Close</th>
                  <th className="text-right py-2.5 px-3 font-medium">Lead→Close</th>
                </tr>
              </thead>
              <tbody>
                {cohortRows.length === 0 && (
                  <tr><td colSpan={8} className="py-4 px-3 text-center text-stone-500">No new leads in this window</td></tr>
                )}
                {cohortRows.map(r => (
                  <tr key={r.channel} className="border-t border-stone-700/70 hover:bg-stone-800/40">
                    <td className="py-2 px-3 font-medium text-white">{r.channel}</td>
                    <td className="py-2 px-3 text-right text-stone-200">{r.leads}</td>
                    <td className="py-2 px-3 text-right text-stone-200">{r.booked}</td>
                    <td className="py-2 px-3 text-right text-stone-200">{r.sat}</td>
                    <td className="py-2 px-3 text-right text-green-400 font-medium">{r.closed}</td>
                    <td className={`py-2 px-3 text-right font-semibold ${pctColor(r.bookedToClose)}`}>{pct(r.bookedToClose)}</td>
                    <td className={`py-2 px-3 text-right font-semibold ${pctColor(r.satToClose)}`}>{pct(r.satToClose)}</td>
                    <td className={`py-2 px-3 text-right font-semibold ${pctColor(r.leadToClose)}`}>{pct(r.leadToClose)}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-stone-600 bg-stone-800/60 font-bold text-white">
                  <td className="py-2.5 px-3">TOTAL</td>
                  <td className="py-2.5 px-3 text-right">{num(cohortTot.leads)}</td>
                  <td className="py-2.5 px-3 text-right">{num(cohortTot.booked)}</td>
                  <td className="py-2.5 px-3 text-right">{num(cohortTot.sat)}</td>
                  <td className="py-2.5 px-3 text-right text-green-400">{num(cohortTot.closed)}</td>
                  <td className={`py-2.5 px-3 text-right ${pctColor(cohortTot.bookedToClose || 0)}`}>{pct(cohortTot.bookedToClose)}</td>
                  <td className={`py-2.5 px-3 text-right ${pctColor(cohortTot.satToClose || 0)}`}>{pct(cohortTot.satToClose)}</td>
                  <td className={`py-2.5 px-3 text-right ${pctColor(cohortTot.leadToClose || 0)}`}>{pct(cohortTot.leadToClose)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Win / Loss reasons */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-stone-800/60 rounded-xl p-4 border border-stone-700" data-testid="coach-win-reasons">
            <div className="flex items-center gap-1.5 text-green-400 text-xs uppercase tracking-wider mb-2"><Award className="w-3.5 h-3.5" /> Why we won</div>
            {Object.keys(winReasons).length === 0 ? (
              <p className="text-sm text-stone-500">No closes in window</p>
            ) : (
              <ul className="space-y-1.5">
                {Object.entries(winReasons).map(([k, v]) => (
                  <li key={k} className="flex justify-between text-sm capitalize">
                    <span className={k === 'not recorded' ? 'text-stone-500 italic' : 'text-stone-200'}>{k}</span>
                    <span className="font-semibold text-white">{v}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="bg-stone-800/60 rounded-xl p-4 border border-stone-700" data-testid="coach-loss-reasons">
            <div className="flex items-center gap-1.5 text-red-400 text-xs uppercase tracking-wider mb-2"><ThumbsDown className="w-3.5 h-3.5" /> Why we lost</div>
            {Object.keys(lostReasons).length === 0 ? (
              <p className="text-sm text-stone-500">No losses in window</p>
            ) : (
              <ul className="space-y-1.5">
                {Object.entries(lostReasons).map(([k, v]) => (
                  <li key={k} className="flex justify-between text-sm capitalize">
                    <span className={k === 'not recorded' ? 'text-stone-500 italic' : 'text-stone-200'}>{k}</span>
                    <span className="font-semibold text-white">{v}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Data notes */}
        {notes.length > 0 && (
          <div className="bg-stone-800/40 rounded-lg p-3 border border-stone-700/60">
            <div className="flex items-center gap-1.5 text-stone-400 text-xs font-medium mb-1.5"><Info className="w-3.5 h-3.5" /> How these numbers are calculated</div>
            <ul className="space-y-1 text-xs text-stone-400 list-disc pl-4">
              {notes.map((n, i) => (<li key={i}>{n}</li>))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
